/**
 * Helper: JSON Convert
 * -----------------------------------------------------------------------------
 * Convert JSON into different cases.
 *
 */

import { convertToHandle } from '~/helpers/convert';
import { genericFileQuery } from '~graphql/generic-file.gql';

/**
 * Convert JSON into a table.
 * @param {Object} data - JSON to convert.
 * @returns {String}
 */
export function convertJsontoTable(data) {
  if (!data) {
    return '';
  }

  const content = JSON.parse(data);

  /**
   * Create table element.
   */
  const table = document.createElement('table');
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');

  table.classList.add('data-table');
  table.appendChild(thead);
  thead.appendChild(headerRow);

  /**
   * Add th elements for each column.
   */
  content.columns?.forEach((column, index) => {
    const th = document.createElement('th');
    th.textContent = column.label;
    const cellLabel = index === 0 ? 'value' : convertToHandle(column.label);
    th.setAttribute('data-column', cellLabel);
    headerRow.appendChild(th);
  });

  /**
   * Create tbody element and append it to the table.
   */
  const tbody = document.createElement('tbody');
  table.appendChild(tbody);

  /**
   * Add rows to the tbody.
   */
  content.items?.forEach((item) => {
    const tr = document.createElement('tr');

    item.forEach((cell, index) => {
      const td = document.createElement('td');
      td.textContent = cell.label;
      const cellLabel =
        index === 0 ? 'value' : convertToHandle(content.columns[index].label);
      td.setAttribute('data-column', cellLabel);
      tr.appendChild(td);
    });

    tbody.appendChild(tr);
  });

  const dropdownWrapper = document.createElement('div');
  const dropdown = document.createElement('select');

  dropdownWrapper.classList.add('form-group');
  dropdown.classList.add('product-information__dropdown');
  dropdownWrapper.appendChild(dropdown);

  /**
   * Duplicate all but the first column and add them to the dropdown.
   */
  const duplicatedColumns = content.columns?.slice(1);

  duplicatedColumns?.forEach((column) => {
    const option = document.createElement('option');
    const optionLabel = convertToHandle(column.label);
    option.textContent = column.label;
    option.setAttribute('value', optionLabel);
    dropdown.appendChild(option);
  });

  const container = document.createElement('div');
  container.appendChild(dropdownWrapper);
  container.appendChild(table);

  if (duplicatedColumns && duplicatedColumns.length > 0) {
    const firstColumnLabel = convertToHandle(duplicatedColumns[0].label);
    const allCells = table.querySelectorAll('td, th');
    allCells.forEach((cell) => {
      const cellColumn = cell.getAttribute('data-column');
      if (cellColumn !== firstColumnLabel && cellColumn !== 'value') {
        cell.style.display = 'none';
      }
    });
  }

  return container;
}

/**
 * Show/hide table data based on dropdown selection.
 */
function updateTableDisplay(dropdown, container) {
  const selectedColumn = convertToHandle(dropdown.value);
  const table = container.querySelector('.data-table');
  const allCells = table.querySelectorAll('td, th');

  allCells.forEach((cell) => {
    const cellColumn = cell.getAttribute('data-column');
    cell.style.display =
      cellColumn === selectedColumn || cellColumn === 'value' ? '' : 'none';
  });
}

/**
 * Get dropdown selection.
 */
export function updateTableContents() {
  document
    .querySelectorAll('.product-information__dropdown')
    .forEach((dropdown) => {
      const container = dropdown.closest('.accordion-snippet__table');
      updateTableDisplay(dropdown, container);

      dropdown.addEventListener('change', () => {
        updateTableDisplay(dropdown, container);
      });
    });
}

/**
 * Trigger Lab Analysis modal window.
 */
export function modalWindow() {
  window.addEventListener('click', (event) => {
    if (event.target.matches('[js-accordion-content="button"]')) {
      const data = {
        namespace: 'analysisModal',
        ignoreDismissed: true,
      };
      cnvs.EventBus.emit('overlay:open', data);
    }
  });
}

/**
 * Fetch PDF URLs as generic files as metaobject has only the GID.
 * Only go by batchSize to minimize delay in performance
 * @param {Array} gidList - List of GIDs to fetch.
 */
export async function getAssetUrls(gidList, batchSize) {
  const batches = Array.from(
    { length: Math.ceil(gidList.length / batchSize) },
    (_, index) => gidList.slice(index * batchSize, (index + 1) * batchSize),
  );

  try {
    const responses = await Promise.all(
      batches.map((batch) =>
        cnvs
          .Query({
            query: genericFileQuery,
            variables: { gids: batch },
          })
          .catch((error) => {
            cnvs.ReportError('Error fetching batch:', error);
            return null;
          }),
      ),
    );

    const allResults = {};

    responses.forEach((response) => {
      if (response?.nodes) {
        response.nodes.forEach((node) => {
          if (node) {
            allResults[node.id] = node.url;
          }
        });
      }
    });

    return allResults;
  } catch (error) {
    cnvs.ReportError('Error fetching batch:', error);
    return null;
  }
}

/**
 * Check if product has a available variant where the title matches the query.
 * - If so, use Image und URL of this variant. For display/product page link in search results.
 * @param {Object} product - Formatted product object.
 * @returns {Object} - Image and URL of fitting variant.
 */
export function getFittingVariant(product, searchQuery) {
  if (!product) {
    return {
      image: null,
      url: null,
    };
  }

  const queryStrings = searchQuery.split(' ');

  const variantMatch = product.variants.find((variant) => {
    return (
      variant.availableState !== 'hidden' &&
      variant.available &&
      queryStrings.some((query) =>
        variant.title.toLowerCase().includes(query.toLowerCase()),
      )
    );
  });

  const img = variantMatch?.localizedImage
    ? variantMatch?.localizedImage
    : variantMatch?.featuredImage;

  return {
    image: variantMatch ? img : null,
    url: variantMatch ? variantMatch.url : null,
  };
}
